import type * as THREE from 'three';
import type { IIndicationStrategy } from '../Indications.controller';
import type { IndicationsService } from '../Indications.service';
import { Tools } from '../ToolStateStore';
import type { AngleModel } from './Angle.model';
import { type IObjectViewModel, SceneItemAction } from '@/engine/app/interfaces';
import type { ObservabilityLoggerService } from '@/app.obresvability/loggerService/observabilityLogger.service.ts';
import type { PlanningAlgorithm } from '@/engine/PlanningAlgorithm/PlanningAlgorithm';
import type { ICommandProcessor } from '../../core/interfaces/Command';
import type { IValue } from '@/engine/DataFlow/Graph';
import type { IRemover } from '@/engine/UIPresentation/Interfaces';
import type { IIndicationAdder } from '../interfaces';

export class AngleIndicationStrategy implements IIndicationStrategy {
  private _indicationsService: IndicationsService;

  private _pointCount = 0;

  private _model?: AngleModel;

  private _indicationViewModel?: IObjectViewModel;

  private readonly _loggerService: ObservabilityLoggerService;

  private readonly _commandProcessor: ICommandProcessor;

  private readonly _planningAlgorithm: PlanningAlgorithm;

  private readonly _remover: IRemover;

  private readonly _adder: IIndicationAdder<IValue>;

  public constructor(
    indicationsService: IndicationsService,
    planningAlgorithm: PlanningAlgorithm,
    commandProcessor: ICommandProcessor,
    adder: IIndicationAdder<IValue>,
    remover: IRemover
  ) {
    this._indicationsService = indicationsService;
    this._loggerService = indicationsService.loggerService;
    this._commandProcessor = commandProcessor;
    this._planningAlgorithm = planningAlgorithm;
    this._adder = adder;
    this._remover = remover;
  }

  public async onPointAddAsync(vectorPoint: THREE.Vector3): Promise<void> {
    if (this._pointCount === 0) {
      await this._initializeIndicationAsync();
      this._model?.setFromPoint(vectorPoint);

      this._pointCount++;
      return;
    }

    if (this._pointCount === 1) {
      this._model?.setCornerPoint(vectorPoint);

      this._pointCount++;
      return;
    }

    if (this._pointCount === 2) {
      this._model?.setToPoint(vectorPoint);
      await this._finalizeIndicationAsync();
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-misused-promises -- //TODO: [chebotarev] fix eslint no-misused-promises
  public async onPointMove(point: THREE.Vector3): Promise<void> {
    if (this._pointCount === 0) {
      return;
    }
    if (this._pointCount === 1) {
      this._model?.setCornerPoint(point);
    } else {
      this._model?.setToPoint(point);
      await this._indicationsService.actualizeIndicationAsync();
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-misused-promises -- //TODO: [chebotarev] fix eslint no-misused-promises
  public async dispose(): Promise<void> {
    if (this._pointCount !== 0) {
      await this._indicationsService.rollbackIndicationAsync();
    }
  }

  private async _initializeIndicationAsync(): Promise<void> {
    const { model, viewModel } = await this._indicationsService.initializeIndicationAsync(Tools.Angle);
    this._model = model as AngleModel;
    this._indicationViewModel = viewModel as IObjectViewModel;
  }

  private async _finalizeIndicationAsync(): Promise<void> {
    if (this._indicationViewModel) {
      const viewModel = this._indicationViewModel;
      const model = this._model as IValue;

      // eslint-disable-next-line @typescript-eslint/no-floating-promises -- //TODO: [chebotarev] fix eslint no-floating-promises
      this._indicationsService.removeIndicationAsync(this._indicationViewModel);

      // eslint-disable-next-line @typescript-eslint/no-floating-promises -- //TODO: [chebotarev] fix eslint no-floating-promises
      this._commandProcessor.executeAnonymousCommand(
        (): Promise<undefined> => {
          // eslint-disable-next-line @typescript-eslint/no-floating-promises -- //TODO: [chebotarev] fix eslint no-floating-promises
          this._adder.addIndication(model, viewModel);
          return Promise.resolve(undefined);
        },
        (): Promise<undefined> => {
          this._remover.removeByDescriptor(viewModel.descriptor);
          return Promise.resolve(undefined);
        }
      );
      this._loggerService.dispatchSceneItemAction(SceneItemAction.IndicationAdded, this._indicationViewModel);
    }
    this._pointCount = 0;
    this._indicationViewModel = undefined;
    this._model = undefined;
    await this._indicationsService.finalizeIndicationAsync();
  }
}
